import { isSessionDraftToolName } from '../../../contracts/approval-kinds';
import type {
  LLMStreamEnvelope,
  LLMStreamEvent,
  MessageBlock,
} from '../../../contracts/harness.types';
import { readToolCallDisplay } from '../../../contracts/tool-call-display';
import { parseApprovalOverrideScopes } from '../approvals/override-scope';
import { readChildAgentRunMetadata } from '../child-runs/metadata';
import { compareStreamIds, streamIdToMs } from '../stream/stream-ids';
import { readToolCallProviderMetadata } from '../tool-call-provider-metadata';
import { observeFrame, presentIdentity } from './identity';
import {
  artifactKey,
  failureKey,
  streamMessageKey,
  streamSlotKey,
  toolKey,
} from './keys';
import {
  appendFailure,
  appendPositional,
  beginDraft,
  bindKeys,
  commitDraft,
  ensureToolEntry,
  mintPositionalId,
  mintStreamSlot,
  resolveEntryId,
  writeEntry,
  type TranscriptDraft,
} from './store';
import type {
  AssistantTranscriptEntry,
  LiveToolStatus,
  ToolTranscriptEntry,
  TranscriptStore,
} from './types';

/**
 * INGESTOR 2 — the stream. Deltas accumulate under the identity the event
 * carries, so an activity retry re-streaming the same `artifact_id` upserts
 * harmlessly; without identity fields a store-local slot stands in until a
 * fetch twin-matches it.
 *
 * `baseCursor` is the snapshot's own cursor: an event at or below it is already
 * persisted, and replaying its deltas would double the text.
 */
export const ingestStreamEvent = (
  store: TranscriptStore,
  envelope: LLMStreamEnvelope,
  baseCursor: string | null = null
): TranscriptStore => {
  if (store.lastStreamId && compareStreamIds(envelope.id, store.lastStreamId) <= 0) {
    return store;
  }
  if (baseCursor && compareStreamIds(envelope.id, baseCursor) <= 0) return store;

  const draft = beginDraft(store);
  draft.lastStreamId = envelope.id;
  draft.changed = true;
  reduceStreamEvent(draft, envelope);
  return commitDraft(store, draft);
};

const reduceStreamEvent = (draft: TranscriptDraft, envelope: LLMStreamEnvelope): void => {
  const event = envelope.event;
  const frameMs = streamIdToMs(envelope.id);
  switch (event.type) {
    case 'message_start':
      reopenAssistantEntry(draft, event, frameMs);
      return;
    case 'text_delta':
      appendToLastBlock(draft, event, 'text', event.text ?? '', frameMs);
      return;
    case 'thinking_delta':
      appendToLastBlock(draft, event, 'thinking', event.text ?? '', frameMs);
      return;
    case 'tool_call_start':
      reduceToolCallStart(draft, event, frameMs);
      return;
    case 'tool_input_delta':
      reduceToolInputDelta(draft, event, frameMs);
      return;
    case 'tool_call_complete':
      appendToolCallBlock(draft, event, frameMs);
      persistCompletedToolInput(draft, event, frameMs);
      if (event.tool_call_id) {
        updateToolFromEvent(draft, event.tool_call_id, event, frameMs);
      }
      return;
    case 'tool_execution_start': {
      if (!event.tool_call_id) return;
      const child = readChildAgentRunMetadata(event.metadata);
      updateToolFromEvent(draft, event.tool_call_id, event, frameMs, (entry) => ({
        ...entry,
        liveStatus: 'running',
        ...(child && { childRun: child }),
      }));
      return;
    }
    case 'tool_execution_delta':
      if (!event.tool_call_id) return;
      updateToolFromEvent(draft, event.tool_call_id, event, frameMs, (entry) => ({
        ...entry,
        liveOutput: `${entry.liveOutput ?? ''}${event.text ?? ''}`,
      }));
      return;
    case 'tool_execution_complete':
      setLiveStatus(draft, event, frameMs, 'complete');
      return;
    case 'tool_execution_error':
      setLiveStatus(draft, event, frameMs, 'error');
      return;
    case 'tool_result_blocks':
      for (const block of event.tool_result_blocks ?? []) {
        updateToolFromEvent(draft, block.tool_call_id, event, frameMs, (entry) =>
          entry.source === 'persisted' && entry.result
            ? entry
            : { ...entry, result: block }
        );
      }
      return;
    case 'approval_requested':
      reduceApprovalRequested(draft, event, frameMs);
      return;
    case 'approval_resolved':
      reduceApprovalResolved(draft, event, frameMs);
      return;
    case 'message_end': {
      const entry = targetAssistantEntry(draft, event, { create: false }, frameMs);
      if (entry)
        writeEntry(draft, observeAssistantFrame({ ...entry, streaming: false }, frameMs));
      draft.activeAssistantId = null;
      draft.streamDiagnostics = {
        usage: event.usage ?? null,
        stop_reason: event.stop_reason ?? null,
        provider_message_id: event.provider_message_id ?? null,
        model: event.model ?? null,
      };
      draft.changed = true;
      return;
    }
    case 'prompt_context_failed': {
      const key = failureKey(envelope.id);
      appendFailure(
        draft,
        {
          id: key,
          kind: 'prompt_context_failure',
          source: 'streamed',
          eventId: envelope.id,
          // Server-provided text crosses verbatim; `null` means "no detail" and
          // the rendering surface supplies its localized fallback line. A blank
          // string carries no detail either, so it normalizes to `null` too.
          text:
            typeof event.metadata.message === 'string' &&
            event.metadata.message.trim() !== ''
              ? event.metadata.message
              : null,
        },
        key
      );
      return;
    }
    case 'turn_completed':
    case 'turn_cancelled':
    case 'turn_failed':
    case 'error':
      closeTurn(draft);
      return;
    default:
      return;
  }
};

/** The turn ended. Nothing is removed; pending approvals only stop standing, or
 *  stale approval cards stack up across turns. */
const closeTurn = (draft: TranscriptDraft): void => {
  for (const entryId of Object.keys(draft.byId)) {
    const entry = draft.byId[entryId];
    if (entry?.kind === 'assistant' && entry.streaming) {
      draft.byId[entryId] = { ...entry, streaming: false };
      draft.changed = true;
    }
    if (entry?.kind === 'tool' && entry.pendingApproval && !entry.pendingApprovalStale) {
      draft.byId[entryId] = { ...entry, pendingApprovalStale: true };
      draft.changed = true;
    }
  }
  draft.activeAssistantId = null;
};

const assistantKeysFor = (event: LLMStreamEvent): string[] => {
  const keys: string[] = [];
  if (event.artifact_id) keys.push(artifactKey(event.artifact_id));
  if (
    event.turn_id &&
    event.iteration !== undefined &&
    event.message_index !== undefined
  ) {
    keys.push(streamMessageKey(event.turn_id, event.iteration, event.message_index));
  }
  return keys;
};

const observeAssistantFrame = (
  entry: AssistantTranscriptEntry,
  frameMs: number | undefined
): AssistantTranscriptEntry => {
  const seenAtMs = observeFrame(entry.seenAtMs, frameMs);
  return seenAtMs === entry.seenAtMs ? entry : { ...entry, seenAtMs };
};

/**
 * Observe a frame on the entry AND on the block it landed in. Blocks are
 * stamped by index so `blockSeenAtMs` stays aligned with `message.blocks`; a
 * block the ingestor never saw a frame for is simply a hole the fold falls
 * through to the entry stamp.
 */
const observeBlockFrame = (
  entry: AssistantTranscriptEntry,
  index: number,
  frameMs: number | undefined
): AssistantTranscriptEntry => {
  const stamped = observeAssistantFrame(entry, frameMs);
  const stamps = stamped.blockSeenAtMs ?? [];
  const observed = observeFrame(stamps[index], frameMs);
  if (observed === undefined || observed === stamps[index]) return stamped;
  const blockSeenAtMs = [...stamps];
  blockSeenAtMs[index] = observed;
  return { ...stamped, blockSeenAtMs };
};

const openAssistantEntry = (
  draft: TranscriptDraft,
  event: LLMStreamEvent,
  frameMs: number | undefined
): AssistantTranscriptEntry => {
  const keys = assistantKeysFor(event);
  const existingId = resolveEntryId(draft, keys);
  const existing = existingId ? draft.byId[existingId] : undefined;
  if (existing?.kind === 'assistant') {
    const stamped = observeAssistantFrame(existing, frameMs);
    if (stamped !== existing) writeEntry(draft, stamped);
    draft.activeAssistantId = existing.id;
    draft.changed = true;
    return stamped;
  }

  const seenAtMs = observeFrame(undefined, frameMs);
  const entry: AssistantTranscriptEntry = {
    id: mintPositionalId(draft),
    kind: 'assistant',
    source: 'streamed',
    message: { role: 'assistant', blocks: [] },
    streaming: true,
    ...(event.artifact_id !== undefined && { artifactId: event.artifact_id }),
    ...(event.message_index !== undefined && { messageIndex: event.message_index }),
    ...presentIdentity({
      turnId: event.turn_id,
      iteration: event.iteration,
      seenAtMs,
    }),
  };
  appendPositional(draft, entry);
  const identityKeys = keys.length > 0 ? keys : [streamSlotKey(mintStreamSlot(draft))];
  bindKeys(draft, entry.id, identityKeys);
  draft.activeAssistantId = entry.id;
  return entry;
};

/**
 * `message_start` restarts the message: an activity retry re-streams the same
 * artifact under fresh ids the monotonic guard cannot filter, and appending
 * would double the text. A persisted entry is never rebased.
 */
const reopenAssistantEntry = (
  draft: TranscriptDraft,
  event: LLMStreamEvent,
  frameMs: number | undefined
): void => {
  const entry = openAssistantEntry(draft, event, frameMs);
  if (entry.source === 'persisted') return;
  if (entry.streaming && entry.message.blocks.length === 0) return;
  writeEntry(
    draft,
    observeAssistantFrame(
      {
        ...entry,
        message: { ...entry.message, blocks: [] },
        streaming: true,
        blockSeenAtMs: [],
      },
      frameMs
    )
  );
};

/**
 * A reconnect can drop `message_start`, so the delta path creates one too. An
 * event that CARRIES identity is never routed by the active pointer, or a
 * mid-message reconnect pours it into whatever the dropped stream left open.
 */
const targetAssistantEntry = (
  draft: TranscriptDraft,
  event: LLMStreamEvent,
  { create }: { create: boolean },
  frameMs: number | undefined
): AssistantTranscriptEntry | undefined => {
  const keys = assistantKeysFor(event);
  const identified = resolveEntryId(draft, keys);
  const entryId =
    identified ?? (keys.length > 0 ? undefined : (draft.activeAssistantId ?? undefined));
  const entry = entryId ? draft.byId[entryId] : undefined;
  if (entry?.kind === 'assistant') return entry;
  return create ? openAssistantEntry(draft, event, frameMs) : undefined;
};

const appendToLastBlock = (
  draft: TranscriptDraft,
  event: LLMStreamEvent,
  kind: 'text' | 'thinking',
  text: string,
  frameMs: number | undefined
): void => {
  const entry = targetAssistantEntry(draft, event, { create: true }, frameMs);
  if (!entry) return;
  const last = entry.message.blocks.at(-1);
  const blocks: MessageBlock[] =
    last?.kind === kind
      ? [...entry.message.blocks.slice(0, -1), { ...last, text: `${last.text}${text}` }]
      : [...entry.message.blocks, { kind, text }];
  writeEntry(
    draft,
    observeBlockFrame(
      { ...entry, message: { ...entry.message, blocks } },
      blocks.length - 1,
      frameMs
    )
  );
};

const appendToolCallBlock = (
  draft: TranscriptDraft,
  event: LLMStreamEvent,
  frameMs: number | undefined
): void => {
  if (!event.tool_call_id || !event.tool_name) return;
  const entry = targetAssistantEntry(draft, event, { create: true }, frameMs);
  if (!entry) return;
  const existingIndex = entry.message.blocks.findIndex(
    (block) => block.kind === 'tool_call' && block.id === event.tool_call_id
  );
  if (existingIndex >= 0) {
    const stamped = observeBlockFrame(entry, existingIndex, frameMs);
    if (stamped !== entry) writeEntry(draft, stamped);
    return;
  }
  const providerMetadata = readToolCallProviderMetadata(event.metadata);
  const blocks: MessageBlock[] = [
    ...entry.message.blocks,
    {
      kind: 'tool_call',
      id: event.tool_call_id,
      name: event.tool_name,
      input: event.tool_input ?? {},
      input_merge: event.tool_input_merge ?? null,
      ...(providerMetadata && { provider_metadata: providerMetadata }),
    },
  ];
  writeEntry(
    draft,
    observeBlockFrame(
      { ...entry, message: { ...entry.message, blocks } },
      blocks.length - 1,
      frameMs
    )
  );
};

const existingTool = (
  draft: TranscriptDraft,
  toolCallId: string
): ToolTranscriptEntry | undefined => {
  const entryId = draft.keys[toolKey(toolCallId)];
  const entry = entryId ? draft.byId[entryId] : undefined;
  return entry?.kind === 'tool' ? entry : undefined;
};

const isSessionDraftStream = (draft: TranscriptDraft, event: LLMStreamEvent): boolean => {
  if (event.tool_name) return isSessionDraftToolName(event.tool_name);
  if (!event.tool_call_id) return false;
  const existingName = existingTool(draft, event.tool_call_id)?.liveToolName;
  return Boolean(existingName && isSessionDraftToolName(existingName));
};

const reduceToolCallStart = (
  draft: TranscriptDraft,
  event: LLMStreamEvent,
  frameMs: number | undefined
): void => {
  if (!event.tool_call_id) return;
  updateToolFromEvent(draft, event.tool_call_id, event, frameMs, (entry) =>
    isSessionDraftToolName(event.tool_name ?? '')
      ? { ...entry, liveToolName: event.tool_name }
      : entry
  );
};

const reduceToolInputDelta = (
  draft: TranscriptDraft,
  event: LLMStreamEvent,
  frameMs: number | undefined
): void => {
  if (!event.tool_call_id) return;
  if (!isSessionDraftStream(draft, event)) {
    updateToolFromEvent(draft, event.tool_call_id, event, frameMs);
    return;
  }
  updateToolFromEvent(draft, event.tool_call_id, event, frameMs, (entry) => ({
    ...entry,
    livePartialInput: `${entry.livePartialInput ?? ''}${event.text ?? ''}`,
    ...(event.tool_name !== undefined ? { liveToolName: event.tool_name } : {}),
  }));
};

/** Keep the last accumulated JSON so the draft card does not flicker before approval. */
const persistCompletedToolInput = (
  draft: TranscriptDraft,
  event: LLMStreamEvent,
  frameMs: number | undefined
): void => {
  if (!event.tool_call_id || !isSessionDraftStream(draft, event)) return;
  const serialized =
    event.tool_input !== undefined ? JSON.stringify(event.tool_input) : undefined;
  updateToolFromEvent(draft, event.tool_call_id, event, frameMs, (entry) => ({
    ...entry,
    ...(serialized !== undefined ? { livePartialInput: serialized } : {}),
    ...(event.tool_name !== undefined ? { liveToolName: event.tool_name } : {}),
  }));
};

const updateTool = (
  draft: TranscriptDraft,
  toolCallId: string,
  update: (entry: ToolTranscriptEntry) => ToolTranscriptEntry
): void => {
  const entry = ensureToolEntry(draft, toolCallId, 'streamed');
  const next = update(entry);
  if (next === entry) return;
  writeEntry(draft, next);
};

const updateToolFromEvent = (
  draft: TranscriptDraft,
  toolCallId: string,
  event: LLMStreamEvent,
  frameMs: number | undefined,
  update: (entry: ToolTranscriptEntry) => ToolTranscriptEntry = (entry) => entry
): void => {
  updateTool(draft, toolCallId, (entry) =>
    withEventIdentity(update(entry), event, frameMs)
  );
};

const withEventIdentity = (
  entry: ToolTranscriptEntry,
  event: LLMStreamEvent,
  frameMs: number | undefined
): ToolTranscriptEntry => {
  const identity = presentIdentity({
    turnId: event.turn_id,
    iteration: event.iteration,
    seenAtMs: observeFrame(entry.seenAtMs, frameMs),
  });
  if (
    (identity.turnId === undefined || entry.turnId === identity.turnId) &&
    (identity.iteration === undefined || entry.iteration === identity.iteration) &&
    identity.seenAtMs === entry.seenAtMs
  ) {
    return entry;
  }
  return { ...entry, ...identity };
};

const setLiveStatus = (
  draft: TranscriptDraft,
  event: LLMStreamEvent,
  frameMs: number | undefined,
  status: LiveToolStatus
): void => {
  if (!event.tool_call_id) return;
  updateToolFromEvent(draft, event.tool_call_id, event, frameMs, (entry) => ({
    ...entry,
    liveStatus: status,
  }));
};

// The live event spreads the approval's public context into `metadata`
// beside these keys; the rest is the context the run snapshot carries.
const APPROVAL_ENVELOPE_KEYS: ReadonlySet<string> = new Set([
  'approval_id',
  'approval_kind',
  'approval_timeout_seconds',
  'available_override_scopes',
]);

const liveApprovalContext = (
  metadata: Record<string, unknown>
): Record<string, unknown> | undefined => {
  const entries = Object.entries(metadata).filter(
    ([key]) => !APPROVAL_ENVELOPE_KEYS.has(key)
  );
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
};

const reduceApprovalRequested = (
  draft: TranscriptDraft,
  event: LLMStreamEvent,
  frameMs: number | undefined
): void => {
  const approvalId =
    typeof event.metadata.approval_id === 'string' ? event.metadata.approval_id : null;
  if (!approvalId || !event.tool_call_id || !event.tool_name) return;
  // BE emits this top-level on the payload; metadata is a defensive fallback.
  const overrideScopes =
    parseApprovalOverrideScopes(event.available_override_scopes) ??
    parseApprovalOverrideScopes(event.metadata.available_override_scopes);
  const toolCallDisplay = readToolCallDisplay(event.metadata);
  const approvalKind =
    typeof event.metadata.approval_kind === 'string'
      ? event.metadata.approval_kind
      : undefined;
  const context = liveApprovalContext(event.metadata);
  const toolCallId = event.tool_call_id;
  const toolName = event.tool_name;
  updateToolFromEvent(draft, toolCallId, event, frameMs, (entry) => ({
    ...entry,
    pendingApprovalStale: false,
    pendingApproval: {
      approval_id: approvalId,
      tool_call_id: toolCallId,
      tool_name: toolName,
      tool_input: event.tool_input ?? {},
      ...(event.tool_input_merge ? { tool_input_merge: event.tool_input_merge } : {}),
      ...(overrideScopes ? { available_override_scopes: overrideScopes } : {}),
      ...(toolCallDisplay ? { toolCallDisplay } : {}),
      ...(approvalKind ? { approval_kind: approvalKind } : {}),
      ...(context ? { context } : {}),
    },
  }));
};

const reduceApprovalResolved = (
  draft: TranscriptDraft,
  event: LLMStreamEvent,
  frameMs: number | undefined
): void => {
  const approvalId =
    typeof event.metadata.approval_id === 'string' ? event.metadata.approval_id : null;
  if (!approvalId) return;
  for (const entryId of draft.toolOrder) {
    const entry = draft.byId[entryId];
    if (entry?.kind !== 'tool') continue;
    if (entry.pendingApproval?.approval_id !== approvalId) continue;
    if (entry.pendingApprovalStale) return;
    updateToolFromEvent(draft, entry.toolCallId, event, frameMs, (current) => ({
      ...current,
      pendingApprovalStale: true,
    }));
    return;
  }
};
